import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Cookie } from 'lucide-react'

// GDPR/CCPA cookie consent banner. Stores a lightweight choice in localStorage
// so we only show it once per browser. 'strictly-necessary' means the visitor
// consents to functional/auth cookies; opting in is recorded via a custom event.

const STORAGE_KEY = 'opraiz:consent'

const panelStyle = {
  position: 'fixed',
  left: 16,
  right: 16,
  bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
  zIndex: 9999,
  maxWidth: 640,
  margin: '0 auto',
  display: 'flex',
  gap: 16,
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  padding: '1.25rem',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card-hover)',
  fontFamily: 'var(--font-body)',
}

const iconChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  flex: '0 0 40px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent-soft)',
  color: 'var(--accent-primary)',
}

const bodyStyle = { flex: '1 1 260px', minWidth: 0 }

const titleStyle = {
  display: 'block',
  margin: '0 0 0.25rem',
  fontFamily: 'var(--font-heading)',
  fontSize: '1rem',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: 'var(--charcoal)',
}

const copyStyle = {
  margin: 0,
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  color: 'var(--text-secondary)',
}

const linkStyle = {
  color: 'var(--accent-strong)',
  fontWeight: 600,
  borderRadius: 4,
  textUnderlineOffset: 3,
}

const actionsStyle = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
}

const buttonStyle = { padding: '0.5rem 1rem', fontSize: '0.8125rem' }

function getChoice() {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const panelRef = useRef(null)
  const acceptRef = useRef(null)
  const restoreFocusRef = useRef(null)

  const decide = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value)
      // Non-critical analytics beacon so a consent audit trail exists.
      window.dispatchEvent(new CustomEvent('opraiz:consent', { detail: { value } }))
    } catch {
      /* ignore */
    }
    setVisible(false)
    restoreFocusRef.current?.focus?.()
    restoreFocusRef.current = null
  }

  useEffect(() => {
    if (!getChoice()) {
      const t = setTimeout(() => setVisible(true), 1500)
      return () => clearTimeout(t)
    }
    return undefined
  }, [])

  // Move focus into the dialog so keyboard users are not stranded behind it,
  // and let Escape take the conservative "essential only" path.
  useEffect(() => {
    if (!visible) return undefined
    restoreFocusRef.current = document.activeElement
    const onKey = (e) => {
      if (e.key === 'Escape') decide('essential')
    }
    document.addEventListener('keydown', onKey)
    const raf = requestAnimationFrame(() => acceptRef.current?.focus())
    return () => {
      document.removeEventListener('keydown', onKey)
      cancelAnimationFrame(raf)
    }
  }, [visible])

  if (!visible) return null

  return (
    <div ref={panelRef} role="dialog" aria-labelledby="cookie-consent-title" style={panelStyle}>
      <span style={iconChipStyle} aria-hidden="true">
        <Cookie size={20} />
      </span>
      <div style={bodyStyle}>
        <strong id="cookie-consent-title" style={titleStyle}>
          Your privacy matters
        </strong>
        <p style={copyStyle}>
          We use strictly necessary cookies to keep you signed in and secure. No advertising
          trackers are used. Read our{' '}
          <Link to="/privacy-policy" style={linkStyle}>
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="/terms-of-service" style={linkStyle}>
            Terms
          </Link>
          .
        </p>
      </div>
      <div style={actionsStyle}>
        <button
          ref={acceptRef}
          type="button"
          className="btn btn-primary"
          style={buttonStyle}
          onClick={() => decide('accepted')}
        >
          Accept
        </button>
        <button
          type="button"
          className="btn btn-outline"
          style={buttonStyle}
          onClick={() => decide('essential')}
        >
          Essential only
        </button>
      </div>
    </div>
  )
}
